import React from "react";
import styled from "styled-components";
import { Table, Row, Col, Form } from "antd";
import { ExpandableConfig } from "antd/es/table/interface";
import {
  WarehouseLoggedHours,
  WarehouseLoggedHoursType,
} from "services/BillingEngineService/interfaces/entities/WarehouseLoggedHours";
import {
  ExpandNotesCell,
  NotesCell,
  HoursCell,
  WorkersCountCell,
  EditActionsCell,
  StatusCell,
} from "./EditableHoursTableCells";
import { TableProps } from "antd/lib/table";
import moment from "moment";
import useQueryUpdateHours from "../Api/useQueryUpdateHours";
import useQueryDeleteHours from "../Api/useQueryDeleteHours";
import isLoading from "helperFunctions/_common/isLoading";

interface EditableHoursTable extends TableProps<WarehouseLoggedHours> {
  rows?: any[];
  columns?: any[];
  type: WarehouseLoggedHoursType;
}

const EditableTableContainer = styled.div`
  .ant-form-item {
    margin-bottom: 0;
  }
`;

function EditableHoursTable({ columns: customColumns = [], rows = [], type, loading, ...rest }: EditableHoursTable) {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = React.useState("");

  // there is no prop exposed to set the column title of an expandable column - dive into the dom to do this instead
  React.useEffect(() => {
    const ths = document.querySelectorAll(`th.ant-table-cell.ant-table-row-expand-icon-cell`);

    ths.forEach((th) => {
      th.textContent = "Notes";
    });
  }, []);

  const [update, { status: updateStatus }] = useQueryUpdateHours();
  const [destroy, { status: removeStatus }] = useQueryDeleteHours();

  const rowLoading = isLoading([updateStatus, removeStatus]);

  const isEditing = (record: WarehouseLoggedHours) => record.id === editingId;

  const columns = [
    ...customColumns,
    {
      title: "Date",
      dataIndex: "date",
      render: (isoDateString: string) => moment.utc(isoDateString).format("MM/D/YYYY"),
    },

    {
      title: "# Workers",
      dataIndex: "workersCount",
      editable: true,
      render: (_: any, record: WarehouseLoggedHours) => {
        const editing = isEditing(record);
        return <WorkersCountCell editing={editing} record={record} />;
      },
    },
    {
      title: "Hours",
      dataIndex: "hours",
      editable: true,
      render: (_: any, record: WarehouseLoggedHours) => {
        const editing = isEditing(record);
        return <HoursCell editing={editing} record={record} />;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_: any, record: WarehouseLoggedHours) => <StatusCell record={record} />,
    },
    {
      key: "action",
      width: "180px",
      render: (_: any, record: WarehouseLoggedHours) => {
        const editing = isEditing(record);
        return (
          <EditActionsCell editing={editing} record={record} save={save} cancel={cancel} remove={remove} edit={edit} />
        );
      },
    },
  ];

  const edit = (record: WarehouseLoggedHours) => {
    form.setFieldsValue({
      ...record,
      hoursWorked: record.hoursWorked || 0,
      minutesWorked: record.minutesWorked || 0,
    });

    setEditingId(record.id);
  };

  const remove = (record: WarehouseLoggedHours) => {
    destroy({ record, type });
  };

  const cancel = () => {
    setEditingId("");
  };

  const save = async (record: WarehouseLoggedHours) => {
    form
      .validateFields()
      .then((validated: any) => {
        update({ record: { ...record, ...validated }, type }).then(() => setEditingId(""));
      })
      .catch((e) => {});
  };

  const expandable: ExpandableConfig<WarehouseLoggedHours> = {
    expandIconColumnIndex: 5,
    expandIcon: ({ record, ...props }: any) => {
      const editing = isEditing(record);
      return <ExpandNotesCell editing={editing} record={record} {...props} />;
    },
    expandedRowRender: (record: WarehouseLoggedHours) => {
      const editing = isEditing(record);
      return <NotesCell editing={editing} record={record} />;
    },
    expandedRowKeys: editingId !== "" ? [`${editingId}`] : undefined,
  };

  return (
    <EditableTableContainer>
      <Row>
        <Col span={24}>
          <Form form={form} component={false}>
            <Table
              dataSource={rows}
              columns={columns}
              rowKey={(record: WarehouseLoggedHours) => `${record.id}`}
              expandable={expandable}
              loading={rowLoading || loading}
              {...rest}
            />
          </Form>
        </Col>
      </Row>
    </EditableTableContainer>
  );
}

export default EditableHoursTable;
